import { PunchStatus, PunchType, type PunchLike } from '@construction/shared';

/**
 * 补录打卡的默认值（考勤页 UI 打磨；CR-066 改成日期 + 时间都摆出来、能补整段）。纯函数，表单和用例共用。
 *
 * 客户原话「补录打卡这个也需要优化，现在操作繁琐了点。以及在异常里点击补录打卡需要智能一点」；
 * CR-066 原话「考勤的补录系统中，没有日期选择，只有时间选择。这个会导致一些次日打卡的问题」。
 *
 * 这里只回答**默认填什么**：该补哪种、日期默认填哪天、上午下午点反了换成什么。
 * **合不合法一律交给 shared 的 `checkManualPunches`**（服务端建卡之前查的也是它）——
 * 别在这里再按「当天第几分钟」推 ±1 天：改之前就是那么推的，夜班选错一天会补进后一晚那班里。
 * 时刻一律按**工地当地**算，不经过浏览器时区。
 */

/** `HH:MM` → 当天第几分钟；格式不对给 null */
export function toMinutes(hhmm: string): number | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm);
  if (!m) return null;
  const hours = Number(m[1]);
  const minutes = Number(m[2]);
  if (hours > 23 || minutes > 59) return null;
  return hours * 60 + minutes;
}

/** 当天第几分钟 → `HH:MM`（跨天的取余） */
export function fromMinutes(total: number): string {
  const r = ((total % 1440) + 1440) % 1440;
  return `${String(Math.floor(r / 60)).padStart(2, '0')}:${String(r % 60).padStart(2, '0')}`;
}

/** 一个时刻在工地时区是哪一天、当天第几分钟 */
export function localDateAndMinutes(iso: string, timeZone: string): { date: string; minutes: number } {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(new Date(iso));
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '00';
  return {
    date: `${get('year')}-${get('month')}-${get('day')}`,
    minutes: (Number(get('hour')) % 24) * 60 + Number(get('minute')),
  };
}

/** `YYYY-MM-DD` 加减天数（日期型，按 UTC 算，不经过时区） */
export function shiftDate(date: string, days: number): string {
  const d = new Date(`${date}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/** `backfill-context` 返回的一张卡 */
export interface ContextPunch {
  id: string;
  type: string;
  occurredAt: string;
  status: string;
}

/** 补哪种：补下班卡 / 补上班卡 / 补整段 */
export type FreeKind = 'out' | 'in' | 'shift';

const time = (punch: ContextPunch) => Date.parse(punch.occurredAt);

/** 没被拒的卡，按时间排 */
function live(punches: readonly ContextPunch[]): ContextPunch[] {
  return punches.filter((p) => p.status !== PunchStatus.REJECTED).sort((a, b) => time(a) - time(b));
}

/** 换成 shared `checkManualPunches` 要的样子 */
export function toPunchLike(punch: ContextPunch): PunchLike {
  return {
    id: punch.id,
    type: punch.type as PunchType,
    occurredAt: new Date(punch.occurredAt),
    status: punch.status as PunchStatus,
  };
}

/** 补下班卡时下班那天默认填哪天：时间晚于上班时刻 → 上班那天；否则次日（夜班 23:00 上、07:00 下） */
export function defaultEndDate(startIso: string, hhmm: string, timeZone: string): string {
  const start = localDateAndMinutes(startIso, timeZone);
  const minutes = toMinutes(hhmm);
  return minutes === null || minutes > start.minutes ? start.date : shiftDate(start.date, 1);
}

/** 补上班卡时上班那天默认填哪天：时间早于下班时刻 → 下班那天；否则前一天 */
export function defaultStartDate(endIso: string, hhmm: string, timeZone: string): string {
  const end = localDateAndMinutes(endIso, timeZone);
  const minutes = toMinutes(hhmm);
  return minutes === null || minutes < end.minutes ? end.date : shiftDate(end.date, -1);
}

/** 补整段时下班那天：下班时间晚于上班时间 → 同一天；否则次日 */
export function shiftEndDate(startDate: string, startTime: string, endTime: string): string {
  const start = toMinutes(startTime);
  const end = toMinutes(endTime);
  return start === null || end === null || end > start ? startDate : shiftDate(startDate, 1);
}

/** 他这会儿开着的上班卡：不晚于现在的最后一张没被拒的卡是上班卡（昨晚开始的夜班也算） */
export function openClockIn(punches: readonly ContextPunch[], now: Date): ContextPunch | null {
  const last = live(punches)
    .filter((p) => time(p) <= now.getTime())
    .pop();
  return last?.type === PunchType.IN ? last : null;
}

/**
 * 缺上班卡的那张下班卡：前面紧挨着的不是上班卡。有几张取最近的。
 * `notBefore` 之前的不算 —— 上下文只往前多给了一个最长班次，太靠前的那张，它的上班卡可能在窗口外面。
 */
export function orphanClockOut(punches: readonly ContextPunch[], notBefore: Date): ContextPunch | null {
  let found: ContextPunch | null = null;
  let previous: ContextPunch | null = null;
  for (const punch of live(punches)) {
    if (punch.type === PunchType.OUT && previous?.type !== PunchType.IN && time(punch) >= notBefore.getTime()) {
      found = punch;
    }
    previous = punch;
  }
  return found;
}

/** 页头补录选了人之后默认补哪种：他开着一班 → 补下班；有一张缺上班卡的下班卡 → 补上班；都没有 → 补整段 */
export function defaultFreeKind(punches: readonly ContextPunch[], now: Date, notBefore: Date): FreeKind {
  if (openClockIn(punches, now)) return 'out';
  if (orphanClockOut(punches, notBefore)) return 'in';
  return 'shift';
}

/** 这个时刻之前紧挨着的那张没被拒的卡；给了 `type`，只在它正是这种卡时返回 */
export function punchBefore(punches: readonly ContextPunch[], iso: string, type?: PunchType): ContextPunch | null {
  const at = Date.parse(iso);
  const found = live(punches)
    .filter((p) => time(p) < at)
    .pop();
  return found && (type === undefined || found.type === type) ? found : null;
}

/** 这个时刻之后紧挨着的那张没被拒的卡；给了 `type`，只在它正是这种卡时返回 */
export function punchAfter(punches: readonly ContextPunch[], iso: string, type?: PunchType): ContextPunch | null {
  const at = Date.parse(iso);
  const found = live(punches).find((p) => time(p) > at);
  return found && (type === undefined || found.type === type) ? found : null;
}

/** 上午 / 下午点反了（5:05 当成了 17:05）：同一个钟面时间换到另外半天 */
export function flipHalfDay(hhmm: string): string {
  const minutes = toMinutes(hhmm);
  return minutes === null ? hhmm : fromMinutes(minutes + 720);
}
